import { shareInFlight } from '../utils/inFlight';

// Command Center loads these lists from the page, its sidebar and the task panel
// within a second or two — reuse one result briefly instead of refetching.
const REUSE_LIST_MS = 10_000;

export const MOMENTUM_AI_ERROR_EVENT = 'momentum-ai-error';

// ── Failed-run detection ───────────────────────────────────────────────────────
// A run that stops without an answer still ends with the terminal `answer`
// event (responseStreaming === false); the failure rides on it as
// error_message (user-facing text) + error_code. BUDGET_REACHED means the
// task's AI session hit its per-conversation spending budget (server default
// $8) and was paused — later runs on the same task fail the same way until the
// budget is raised or the task is handed to a fresh session.
export const BUDGET_REACHED_CODE = 'BUDGET_REACHED';
export interface KanbanRunError { message: string; code: string; }
export const kanbanRunError = (evt: any): KanbanRunError | null => {
  if (!evt || evt.type !== 'answer' || evt.responseStreaming !== false) return null;
  const message = typeof evt.error_message === 'string' ? evt.error_message.trim() : '';
  if (!message) return null;
  return { message, code: typeof evt.error_code === 'string' ? evt.error_code : '' };
};

// ── SSE reader with smoothed answer reveal ─────────────────────────────────────
// The kanban stream delivers the final answer as live token fragments
// (Managed-Agents event_deltas previews — same mechanism as /claude-managed/chat).
// Previews are best-effort: normally the text arrives as a steady trickle, but
// under load the platform sheds deltas and the missing tail lands as one big
// buffered chunk. Rendering events exactly as they arrive therefore looks rough —
// smooth typing, a stall, then a wall of text.
//
// Mirroring claudeAgentApi.ts's pumpSse, receipt is decoupled from reveal:
// incoming answer text (responseStreaming !== false) goes into a queue and a
// ticker releases it as `answer` chunks at a steady cadence, sized adaptively to
// the backlog — a live trickle passes through at reading speed, a shed-delta
// dump animates out over roughly a second. The terminal answer event
// (responseStreaming === false, carrying the full text / status / tool_history)
// is held until the queue drains so it still closes the turn last. Non-answer
// events (thought, tool announcements, errors) flush the queue first and then
// pass through immediately — that preserves the exact event order the panel's
// turn-boundary logic depends on, and never delays real interactions.
//
// `onEvent` returning 'stop' ends the read early (the terminal event).
// `replayDelay` covers a buffered agent run delivering its whole stream in one
// network chunk: the thought tokens are paced out so the thinking is actually
// visible before the answer arrives and hides it. Re-attach passes false: its
// first chunk is a catch-up backlog the user should see applied at once.
const ANSWER_TICK_MS    = 35;   // reveal cadence
const ANSWER_DRAIN_PART = 20;   // per tick, reveal 1/20 of the backlog…
const ANSWER_MIN_CHARS  = 4;    // …but at least this many characters

export const consumeKanbanSse = async (
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  onEvent: (event: any) => 'stop' | void,
  replayDelay = true,
): Promise<void> => {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';
  let firstChunkSeen = false;
  let stopped = false;

  let template: any = null;                 // latest partial answer's fields, reused on reveals
  let queue = '';                           // answer text received but not yet revealed
  let terminal: any = null;                 // terminal answer, held until the queue drains
  let timer: ReturnType<typeof setInterval> | null = null;
  let wake: (() => void) | null = null;     // resolves the end-of-stream drain wait

  const stopTimer = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
  const deliver = (evt: any) => { if (!stopped && onEvent(evt) === 'stop') stopped = true; };
  const emitTerminal = () => {
    if (!terminal) return;
    const t = terminal;
    terminal = null;
    deliver(t);
  };
  const revealTick = () => {
    if (stopped || signal.aborted) {
      stopTimer();
      queue = '';
      terminal = null;
      if (wake) { const w = wake; wake = null; w(); }
      return;
    }
    if (queue && template) {
      const n = Math.max(ANSWER_MIN_CHARS, Math.ceil(queue.length / ANSWER_DRAIN_PART));
      const slice = queue.slice(0, n);
      queue = queue.slice(n);
      deliver({ ...template, response: slice });
    }
    if (!queue || stopped) {
      stopTimer();
      emitTerminal();
      if (wake) { const w = wake; wake = null; w(); }
    }
  };
  const startTimer = () => { if (timer === null) timer = setInterval(revealTick, ANSWER_TICK_MS); };
  // Correctness path — abort or a thrown error mustn't swallow received text,
  // so the animation is skipped and everything left is delivered at once.
  const flushAllNow = () => {
    stopTimer();
    if (queue && template) { deliver({ ...template, response: queue }); queue = ''; }
    emitTerminal();
  };

  try {
    while (!stopped) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const evts = buf.split(/\r?\n\r?\n/);
      buf = evts.pop() ?? '';
      const parsedEvents: any[] = [];
      for (const evt of evts) {
        const dl = evt.split(/\r?\n/).find(l => l.startsWith('data:'));
        if (!dl) continue;
        const payload = dl.replace(/^data:\s*/, '').trim();
        if (!payload || payload === '[DONE]') continue;
        try { parsedEvents.push(JSON.parse(payload)); } catch { /* skip malformed */ }
      }
      const thoughtCount = replayDelay
        ? parsedEvents.filter(e => e?.type === 'thought' && e.thought).length
        : 0;
      const thoughtDelay = thoughtCount > 0 ? Math.min(24, Math.max(6, Math.round(4000 / thoughtCount))) : 0;
      if (!firstChunkSeen && thoughtDelay === 0) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
      firstChunkSeen = true;
      for (const parsed of parsedEvents) {
        if (signal.aborted || stopped) return;
        if (thoughtDelay && parsed.type === 'thought' && parsed.thought) {
          await new Promise(r => setTimeout(r, thoughtDelay));
        }
        if (parsed.type === 'answer' && parsed.responseStreaming !== false && parsed.response) {
          template = parsed;
          queue += parsed.response;
          startTimer();
        } else if (parsed.type === 'answer' && parsed.responseStreaming === false) {
          terminal = parsed;
          if (!queue) { stopTimer(); emitTerminal(); }
          // else: revealTick emits it once the queue drains
        } else {
          // Ordering guard: reveal everything already received before handing
          // over a non-answer event — the panel's turn-boundary reset (a
          // thought after answer text starts a new turn) depends on it.
          if (queue && template) { stopTimer(); deliver({ ...template, response: queue }); queue = ''; }
          deliver(parsed);
        }
        if (stopped) return;
      }
    }
    // Stream closed with text still animating — let the reveal finish so the
    // caller's "stream ended" logic doesn't cut it off. The adaptive tick
    // bounds this wait to roughly a second regardless of size.
    if ((queue || terminal) && !stopped) {
      startTimer();
      await new Promise<void>((resolve) => { wake = resolve; });
    }
  } finally {
    flushAllNow();
  }
};

export const createKanbanTransactionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const resolveTaskTransactionId = async (taskId: string): Promise<string | null> => {
  const authToken = localStorage.getItem('authToken') || '';
  try {
    const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/momentumkanban/task/${taskId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return null;
    const json = await res.json();
    const transactionId = json?.data?.transactionId;
    return typeof transactionId === 'string' && transactionId ? transactionId : null;
  } catch {
    return null;
  }
};

export type KanbanCustomCategory = { id: number; name: string };

// Custom (user-created) kanban categories, appended to the built-in TYPE
// options (ids 1–8) wherever tasks are categorized. The backend accepts a
// custom category's row id directly as `categoryId` on task upsert.
/**
 * Raw custom/list response ({ data: [...] }, full rows). Rejects on a non-OK
 * response. The Command Center page and its config sidebar both load this on
 * mount, so concurrent calls share one request.
 */
export const fetchKanbanCustomCategoriesRaw = (): Promise<any> => {
  const authToken = localStorage.getItem('authToken') || '';
  return shareInFlight(`kanbanCustomCategories|${authToken}`, async () => {
    const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumKanban/task/category/custom/list`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) throw new Error(`Failed to load custom categories (${res.status})`);
    return res.json();
  }, REUSE_LIST_MS);
};

export const listKanbanCustomCategories = async (): Promise<KanbanCustomCategory[]> => {
  try {
    const json = await fetchKanbanCustomCategoriesRaw();
    return (json?.data ?? [])
      .filter((c: any) => c?.id != null && c?.name)
      .map((c: any) => ({
        id: Number(c.id),
        name: c.channel === 2 ? `${c.name} (Voice)` : c.channel === 3 ? `${c.name} (SMS)` : String(c.name),
      }));
  } catch {
    return [];
  }
};

export const callMomentumKanbanAI = (taskId: string, onError?: (message: string) => void): void => {
  const authToken = localStorage.getItem('authToken') || '';
  const ncToken = localStorage.getItem('amsToken') || '';
  const formData = new FormData();
  formData.append('taskId', taskId);
  formData.append('nc_token', ncToken);
  fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
    body: formData,
  })
    .then(async (res) => {
      if (res.ok) return;
      let msg = `AI request failed (${res.status})`;
      try {
        const json = await res.json();
        msg = json?.errorMessage || json?.message || msg;
      } catch {
        try {
          const text = await res.text();
          if (text) msg = text;
        } catch {
          // Ignore parse failures and keep fallback message.
        }
      }
      throw new Error(msg);
    })
    .catch((err: unknown) => {
      const message = (err as { message?: string })?.message || 'Failed to start AI processing.';
      onError?.(message);
      window.dispatchEvent(new CustomEvent(MOMENTUM_AI_ERROR_EVENT, { detail: message }));
    });
};

export const callMomentumKanbanStream = async (
  taskId: string,
  prompt: string,
  onError?: (message: string) => void,
): Promise<void> => {
  const authToken = localStorage.getItem('authToken') || '';
  const ncToken = localStorage.getItem('amsToken') || '';
  const formData = new FormData();
  formData.append('taskId', taskId);
  formData.append('nc_token', ncToken);
  formData.append('prompt', prompt);

  try {
    const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban-stream`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    });

    if (!res.ok || !res.body) {
      let msg = `AI stream failed (${res.status})`;
      try {
        const json = await res.json();
        msg = json?.errorMessage || json?.message || msg;
      } catch {
        try {
          const text = await res.text();
          if (text) msg = text;
        } catch {
          // Keep fallback message.
        }
      }
      throw new Error(msg);
    }

    // Consume the SSE stream so the request stays active until completion, and
    // surface a run that ended in failure (terminal answer carrying
    // error_message — e.g. BUDGET_REACHED) exactly like a failed request: the
    // panel that fired this has already closed, so the board-level toast
    // (MOMENTUM_AI_ERROR_EVENT) is the only channel left to tell the user.
    // replayDelay=false: nobody is watching this stream animate.
    const runError: { current: KanbanRunError | null } = { current: null };
    await consumeKanbanSse(res.body, new AbortController().signal, (evt) => {
      const err = kanbanRunError(evt);
      if (err) runError.current = err;
      if (evt?.type === 'answer' && evt.responseStreaming === false) return 'stop';
    }, false);
    if (runError.current) throw new Error(runError.current.message);
  } catch (err: unknown) {
    const message = (err as { message?: string })?.message || 'Failed to start AI streaming execution.';
    onError?.(message);
    window.dispatchEvent(new CustomEvent(MOMENTUM_AI_ERROR_EVENT, { detail: message }));
  }
};

// ── Live run re-attach (page refresh mid-execution) ───────────────────────────
// A /momentum-kanban-stream run keeps executing server-side even if the page is
// refreshed: the backend drives the agent on a task detached from the HTTP
// connection and mirrors every SSE line into a per-run event log. SSE itself has
// no replay, so a refreshed client asks getActiveKanbanRun whether a run is
// still in flight and, if so, calls openKanbanReattachStream — a live stream of
// the same `thought`/`answer` events the POST emits, starting with everything it
// missed.

export interface ActiveKanbanRun {
  active: boolean;
  chatId?: string;
  startedAt?: string | null;
}

export const getActiveKanbanRun = async (taskId: string): Promise<ActiveKanbanRun> => {
  const authToken = localStorage.getItem('authToken') || '';
  try {
    const res = await fetch(
      `${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban-stream/active/${encodeURIComponent(taskId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } },
    );
    if (!res.ok) return { active: false };
    const json = await res.json();
    return {
      active: !!json?.active,
      chatId: typeof json?.chatId === 'string' ? json.chatId : undefined,
      startedAt: json?.startedAt ?? null,
    };
  } catch {
    // Treat an unreachable/older backend as "nothing in flight" — the panel then
    // behaves exactly as it did before re-attach existed.
    return { active: false };
  }
};

// Opens the re-attach stream and returns its response body for the caller to
// consume with the same SSE reader it uses for the POST stream. `since` is the
// number of events already applied, so a second re-attach on the same run does
// not replay what is already on screen.
export const openKanbanReattachStream = async (
  taskId: string,
  signal?: AbortSignal,
  since = 0,
): Promise<ReadableStream<Uint8Array> | null> => {
  const authToken = localStorage.getItem('authToken') || '';
  const res = await fetch(
    `${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban-stream/reattach/${encodeURIComponent(taskId)}?since=${Math.max(0, since)}`,
    { headers: { Authorization: `Bearer ${authToken}` }, signal },
  );
  if (!res.ok || !res.body) return null;
  return res.body;
};

export interface CreatedKanbanTask {
  taskId: number;
  taskNumber: string;
  title: string;
  description: string;
  statusId: string;
  assigneeType: number;
  source: number;
}

// Partial fields surfaced while the create stream is still in flight.
export interface KanbanCreateStreamUpdate {
  title?: string;
  description?: string;
  descriptionComplete?: boolean;
}

export interface MomentumKanbanUploadResponse {
  fileUrl?: string;
  fileName?: string;
  transactionId: string;
  [key: string]: any;
}

export const uploadMomentumKanbanFile = async (
  file: File,
  transactionId?: string,
): Promise<MomentumKanbanUploadResponse> => {
  const authToken = localStorage.getItem('authToken') || '';
  const txId = transactionId || createKanbanTransactionId();
  const formData = new FormData();
  formData.append('formFile', file);

  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumKanban/upload/${txId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      Accept: 'application/json, text/plain, */*',
      'X-Transaction-Id': txId,
    },
    body: formData,
  });

  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const json = await res.json();
      msg = json?.errorMessage || json?.message || msg;
    } catch {
      try {
        const text = await res.text();
        if (text) msg = text;
      } catch {
        // Keep fallback message.
      }
    }
    throw new Error(msg);
  }

  const data = await res.json().catch(() => ({}));
  const resolvedTransactionId = typeof data?.transactionId === 'string' && data.transactionId
    ? data.transactionId
    : txId;
  return { ...(data || {}), transactionId: resolvedTransactionId };
};

// One folder's worth of AMS-drive files to attach to a task. The backend lists
// the folder via NowCerts and filters by amsFileIds, so files selected across
// multiple folders must be sent as one payload per folder.
export interface AmsFilesUploadPayload {
  insuredId: string;
  folderId: string;
  amsFileIds: string[];
}

// Attaches AMS-drive files to a kanban transaction entirely server-side: the
// API resolves the NowCerts download URLs, fetches the bytes and stores them in
// the same blob path as uploadMomentumKanbanFile. This avoids the browser
// fetching the pre-signed S3 URLs directly (they are not CORS-enabled).
export const uploadMomentumKanbanAmsFiles = async (
  payload: AmsFilesUploadPayload,
  transactionId?: string,
): Promise<{ files: MomentumKanbanUploadResponse[]; transactionId: string }> => {
  const authToken = localStorage.getItem('authToken') || '';
  const ncToken = localStorage.getItem('amsToken') || '';
  const txId = transactionId || createKanbanTransactionId();

  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumKanban/upload-ams-files/${txId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      'X-Transaction-Id': txId,
    },
    body: JSON.stringify({ ...payload, ncToken }),
  });

  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const json = await res.json();
      msg = json?.errorMessage || json?.message || msg;
    } catch {
      try {
        const text = await res.text();
        if (text) msg = text;
      } catch {
        // Keep fallback message.
      }
    }
    throw new Error(msg);
  }

  const data = await res.json().catch(() => []);
  const files: MomentumKanbanUploadResponse[] = (Array.isArray(data) ? data : []).map((f: any) => ({
    fileUrl: typeof f?.fileUrl === 'string' ? f.fileUrl : undefined,
    fileName: typeof f?.fileName === 'string' ? f.fileName : undefined,
    transactionId: txId,
  }));
  return { files, transactionId: txId };
};

function extractKanbanFileDescriptorUrl(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { fileUrl?: string; url?: string } | string | null;
    if (typeof parsed === 'string') return parsed.trim() || null;
    const url = (parsed?.fileUrl || parsed?.url || '').trim();
    return url || null;
  } catch {
    return null;
  }
}

export const getKanbanFileUrl = async (transactionId: string, fileName: string): Promise<string> => {
  const authToken = localStorage.getItem('authToken') || '';
  const params = new URLSearchParams({ transactionId, fileName });
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumKanban/file?${params.toString()}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });

  if (!res.ok) {
    let msg = `Failed to resolve file (${res.status})`;
    try {
      const json = await res.json();
      msg = json?.errorMessage || json?.message || msg;
    } catch {
      try {
        const text = await res.text();
        if (text) msg = text;
      } catch {
        // Keep fallback message.
      }
    }
    throw new Error(msg);
  }

  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('json')) {
    const url = extractKanbanFileDescriptorUrl(await res.text());
    if (url) return url;
    throw new Error('File URL not found.');
  }

  const blob = await res.blob();
  const looksTextual = contentType === '' || contentType.startsWith('text/') || contentType.includes('octet-stream');
  if (looksTextual && blob.size < 8192) {
    const url = extractKanbanFileDescriptorUrl(await blob.text());
    if (url) return url;
  }
  return URL.createObjectURL(blob);
};

// Sends an email via the kanban send-email proxy (NowCerts DataImports/SendEmail).
export const sendKanbanEmail = async (
  payload: { to: string; subject: string; body: string; cc?: string; taskId?: string; taskNumber?: string },
): Promise<{ success: boolean; to: string }> => {
  const authToken = localStorage.getItem('authToken') || '';
  const ncToken = localStorage.getItem('amsToken') || '';
  const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban/send-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ ...payload, nc_token: ncToken }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).errorMessage || `Send failed (${res.status})`);
  }
  return res.json();
};

// Fetches sent email history for a task (used to restore "Sent" badges on reload).
export interface SentEmailRecord {
  to: string;
  subject: string;
  status: string;
  createdAt: string | null;
}

export const fetchKanbanEmailHistory = async (taskId: string): Promise<SentEmailRecord[]> => {
  const authToken = localStorage.getItem('authToken') || '';
  try {
    const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban/email-history/${encodeURIComponent(taskId)}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json?.data ?? [];
  } catch {
    return [];
  }
};

// Extracts a $$<marker>_START$$...$$<marker>_END$$ block from streamed text.
// While the closing tag hasn't arrived yet, returns whatever content has
// streamed in so far with `complete: false`; returns null before the opening
// tag appears. Marker tags can split across stream chunks, so a trailing
// partial tag (e.g. "$$TASK_DESCRIPTION_EN") is stripped from partial content.
const extractTaskMarker = (
  text: string,
  marker: string,
): { value: string; complete: boolean } | null => {
  const startTag = `$$${marker}_START$$`;
  const endTag = `$$${marker}_END$$`;
  const start = text.indexOf(startTag);
  if (start === -1) return null;
  const contentStart = start + startTag.length;
  const end = text.indexOf(endTag, contentStart);
  if (end !== -1) return { value: text.slice(contentStart, end).trim(), complete: true };
  const partial = text.slice(contentStart).replace(/(?:\$\$[A-Z_]*\$?|\$)$/, '');
  return { value: partial, complete: false };
};

// Optional task/upsert (KanbanTaskModel) fields forwarded verbatim by the
// create endpoint to POST /api/momentumkanban/task/upsert — the backend
// creates the task through that API now, no direct DB insert. Any field left
// null/undefined is derived server-side (assignee + status from the category's
// configured Mode). id/taskNumber are intentionally not part of this shape:
// the endpoint always inserts, and the task number is server-generated.
// title/description/source/categoryId are not part of it either — those always
// come from the AI response (source directly, categoryId from the classified
// category_type), and the backend ignores them if sent.
export interface KanbanCreateUpsertOverrides {
  statusId?: string | null;
  comments?: Array<{
    role: string;
    message: string;
    created?: string;
    // Author identity, persisted as UserId/FirstName/LastName on the comment.
    userId?: string;
    firstName?: string;
    lastName?: string;
  }>;
  assigneeId?: number;
  assigneeType?: number | null; // 0=Human, 1=AI, 2=Digital Workforce agent
  lastAssigneeType?: number | null;
  position?: number;
  agentName?: string | null;
  agentId?: string | null;
  files?: Array<{ fileName?: string; fileUrl?: string }> | null;
}

// Calls the streaming create endpoint. The backend emits SSE `data:` events
// shaped {"chatId","response"}; the concatenated `response` text carries each
// task field as $$TASK_TITLE_START$$...$$TASK_TITLE_END$$ marker blocks (the
// description streams in several chunks between its markers). The stream ends
// with {"responseStreaming": false}; a Disabled category ends it early with
// {"skipped": true} (resolved as null, matching the old 204 behavior); errors
// arrive as {"errorMessage"} events. `onUpdate` (optional) receives the title
// and partial description as they stream in, before the promise resolves.
export const createKanbanTaskFromPrompt = async (
  prompt: string,
  onUpdate?: (update: KanbanCreateStreamUpdate) => void,
  transactionId?: string,
  overrides?: KanbanCreateUpsertOverrides,
): Promise<CreatedKanbanTask | null> => {
  const authToken = localStorage.getItem('authToken') || '';
  const ncToken = localStorage.getItem('amsToken') || '';
  const txId = transactionId || createKanbanTransactionId();
  const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban-create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      prompt,
      nc_token: ncToken,
      // task/upsert parameters — null fields are derived by the backend.
      // No title/description/source/categoryId here: the backend takes those
      // straight from the AI response and ignores any client value.
      statusId: overrides?.statusId ?? null,
      comments: overrides?.comments ?? [],
      assigneeId: overrides?.assigneeId ?? 0,
      assigneeType: overrides?.assigneeType ?? null,
      lastAssigneeType: overrides?.lastAssigneeType ?? overrides?.assigneeType ?? null,
      position: overrides?.position ?? 0,
      agentName: overrides?.agentName ?? null,
      agentId: overrides?.agentId ?? null,
      files: overrides?.files ?? null,
      transactionId: txId,
    }),
  });
  if (!res.ok || !res.body) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).errorMessage || `HTTP ${res.status}`);
  }

  let streamText = '';
  let skipped = false;
  const emitUpdate = () => {
    if (!onUpdate) return;
    const title = extractTaskMarker(streamText, 'TASK_TITLE');
    const description = extractTaskMarker(streamText, 'TASK_DESCRIPTION');
    if (!title && !description) return;
    onUpdate({
      title: title?.value,
      description: description?.value,
      descriptionComplete: description?.complete ?? false,
    });
  };
  const consumeEvent = (event: string) => {
    const dataLine = event
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find((l) => l.startsWith('data:'));
    if (!dataLine) return;
    let json: any = null;
    try {
      json = JSON.parse(dataLine.replace(/^data:\s*/, ''));
    } catch {
      return;
    }
    if (json?.errorMessage) throw new Error(json.errorMessage);
    if (json?.skipped) skipped = true; // category Disabled — no task created
    if (typeof json?.response === 'string' && json.response) {
      streamText += json.response;
      emitUpdate();
    }
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let sseBuffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    sseBuffer += decoder.decode(value, { stream: true });
    const events = sseBuffer.split('\n\n');
    sseBuffer = events.pop() ?? '';
    for (const event of events) consumeEvent(event);
  }
  // Flush anything left in the buffer (stream may not end on an event boundary)
  if (sseBuffer.trim()) consumeEvent(sseBuffer);

  if (skipped) return null;

  const taskId = extractTaskMarker(streamText, 'TASK_ID');
  const taskNumber = extractTaskMarker(streamText, 'TASK_NUMBER');
  const title = extractTaskMarker(streamText, 'TASK_TITLE');
  const description = extractTaskMarker(streamText, 'TASK_DESCRIPTION');
  const statusId = extractTaskMarker(streamText, 'TASK_STATUS_ID');
  const assigneeType = extractTaskMarker(streamText, 'TASK_ASSIGNEE_TYPE');
  const source = extractTaskMarker(streamText, 'TASK_SOURCE');
  if (!taskId?.complete || !taskNumber?.complete) {
    throw new Error('Task creation stream ended without a task id');
  }
  return {
    taskId: Number(taskId.value),
    taskNumber: taskNumber.value,
    title: title?.value ?? '',
    description: description?.value ?? '',
    statusId: statusId?.value ?? '',
    assigneeType: Number(assigneeType?.value ?? 0),
    source: Number(source?.value ?? 1),
  };
}

export interface AgencyAgent {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export const fetchAgencyAgents = async (): Promise<AgencyAgent[]> => {
  const authToken = localStorage.getItem('authToken') || '';
  const amsToken = localStorage.getItem('amsToken') || '';
  if (!amsToken) return [];
  const agencyId = localStorage.getItem('NCAgencyId') || '';
  // Command Center resolves "Shared with X" names for every shared task at once —
  // share the request so N shared tasks cost one agentlist call, not N.
  const list = await shareInFlight(`agencyAgents|${authToken}|${agencyId}`, async () => {
    const res = await fetch(
      `${process.env.REACT_APP_Auth_Url}/api/account/agentlist?nctoken=${encodeURIComponent(amsToken)}&agencyId=${encodeURIComponent(agencyId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    if (!res.ok) throw new Error(`Failed to load agents (${res.status})`);
    const data = await res.json();
    return (Array.isArray(data) ? data : (data.value ?? data.data ?? [])) as AgencyAgent[];
  }, REUSE_LIST_MS);
  return [...list];
};

/**
 * GET /api/momentumkanban/task/{id} — the full task (comments, files, shared ids).
 * Opening a command fetches it from the page (detail), the panel (shared agents)
 * and the run-details auto-open at the same moment; concurrent callers share one
 * request. Not cached once it lands — polling and edits always get fresh data.
 * Resolves to the parsed body, or null on a non-OK response. Treat it as read-only.
 */
export const fetchKanbanTaskJson = (taskId: string): Promise<any | null> => {
  const authToken = localStorage.getItem('authToken') || '';
  return shareInFlight(`kanbanTask|${taskId}|${authToken}`, async () => {
    const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/momentumkanban/task/${taskId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return res.ok ? res.json() : null;
  });
};

export const fetchTaskSharedAgentIds = async (taskId: string): Promise<string[]> => {
  const json = await fetchKanbanTaskJson(taskId);
  if (!json) throw new Error('Failed to load task');
  return Array.isArray(json?.data?.sharedAgentIds) ? json.data.sharedAgentIds : [];
};
